import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// Services
import { TABLE_QUERY_KEY, useTableReloadActionMutation } from '../../services/table/tableQueries';
import { useTableDeleteRowOptimistic } from '../../services/table/tableOptimistic';

// Utils
import { normalizeUrl } from '../../utils/url';

/**
 * Hook to manage the row actions
 * @param {object} props
 * @param {string} props.rowId - The row id
 * @param {(formUrl: string) => void} [props.onOpenFormModal] - Handle opening form modal from row action
 * @returns {{
 *   handleRowActionByType: (action: TableRowAction, type: string) => void,
 *   isMutationLoading: boolean,
 *   rowActionsIsOpen: boolean,
 *   setRowActionsIsOpen: (isOpen: boolean) => void,
 *   isDeleteConfirmOpen: boolean,
 *   handleCloseDeleteConfirmModal: () => void,
 *   confirmDeleteRow: () => void,
 *   isDeleteLinePending: boolean,
 * }} The row actions state and actions
 */
const useTableRowActions = ({ rowId = '', onOpenFormModal }) => {
  const [rowActionsIsOpen, setRowActionsIsOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const queryClient = useQueryClient();
  const { mutate: mutateDeleteLine, isPending: isDeleteLinePending } = useTableDeleteRowOptimistic();
  const { mutate: mutateReloadTable, isPending: isReloadPending } = useTableReloadActionMutation();

  // Handlers
  /**
   * Handle the open tab
   * @param {ApiManagerTableActionOpenTabObject} action - The action to handle
   */
  const handleOpenTabModalMutation = (action) => {
    const normalizedUrl = normalizeUrl(action.basicUrl);
    window.open(normalizedUrl, '_blank');
    setRowActionsIsOpen(false);
  };

  /**
   * Handle the reload table mutation
   * @param {ApiManagerTableActionReloadTableObject} action - The action to handle
   */
  const handleReloadTableMutation = (action) => {
    mutateReloadTable(
      { actionUrl: action.actionUrl },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
          setRowActionsIsOpen(false);
        },
      },
    );
  };

  /**
   * Open delete confirmation modal for a row action
   * @param {ApiManagerTableActionDeleteLineObject} action - The action to handle
   */
  const handleDeleteLineMutation = (action) => {
    setRowActionsIsOpen(false);
    setPendingDelete(action);
    setIsDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirmModal = () => {
    setIsDeleteConfirmOpen(false);
    setPendingDelete(null);
  };

  const confirmDeleteRow = () => {
    if (!pendingDelete) return;
    const { deleteUrl } = pendingDelete;
    setIsDeleteConfirmOpen(false);
    setPendingDelete(null);
    mutateDeleteLine({ id: rowId, deleteUrl }, {
      onSuccess: () => {
        setRowActionsIsOpen(false);
      },
    });
  };

  /**
   * Handle opening form modal from row action
   * @param {ApiManagerTableActionOpenFormModalObject} action - The action to handle
   */
  const handleOpenFormModal = (action) => {
    if (onOpenFormModal && action.formUrl) {
      onOpenFormModal(action.formUrl);
      setRowActionsIsOpen(false);
    }
  };

  /**
   * Handle the row action by type
   * @param {TableRowAction} action - The action to handle
   * @param {string} type - The action type
   */
  const handleRowActionByType = (action, type) => {
    switch (type) {
      case 'openFormModal': {
        const actionOpenFormModal = /** @type {ApiManagerTableActionOpenFormModalObject} */ (action);
        handleOpenFormModal(actionOpenFormModal);
        break;
      }
      case 'deleteLine': {
        const actionDeleteLine = /** @type {ApiManagerTableActionDeleteLineObject} */ (action);
        handleDeleteLineMutation(actionDeleteLine);
        break;
      }
      case 'reloadTable': {
        const actionReloadTable = /** @type {ApiManagerTableActionReloadTableObject} */ (action);
        handleReloadTableMutation(actionReloadTable);
        break;
      }
      case 'openTab': {
        const actionOpenTab = /** @type {ApiManagerTableActionOpenTabObject} */ (action);
        handleOpenTabModalMutation(actionOpenTab);
        break;
      }
      default: {
        // eslint-disable-next-line no-console
        console.warn(`Action type ${type} not supported`);
        break;
      }
    }
  };

  return {
    handleRowActionByType,
    isMutationLoading: isDeleteLinePending || isReloadPending,
    // Popover
    rowActionsIsOpen,
    setRowActionsIsOpen,
    isDeleteConfirmOpen,
    handleCloseDeleteConfirmModal,
    confirmDeleteRow,
    isDeleteLinePending,
  };
};

export default useTableRowActions;
